'use client';

import { useCallback, useMemo, type ReactNode } from 'react';
import { useFormatter, useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Button } from '@terra/ui-design-system';
import type { SkillSummaryDto } from '@terra/shared-contracts-ai-skills';
import {
  SkillsManagerPanel,
  type SkillsEditorLabels,
  type SkillsExplainerLabels,
} from '@terra/ui-skills';
import { useAppTargetId } from '../../store/projectStore';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useMainContentTabs } from '../../store/layoutStore';
import {
  EMPTY_SKILL_DRAFT,
  useSkillDraftsStore,
  type SkillDraft,
} from '../../store/skillDraftsStore';
import { useChatTabs } from '../../store/chatTabsStore';
import { useCreateNewChatTab } from '../chat/actions/useCreateNewChatTab';
import { v4 as uuidv4 } from 'uuid';
import { createSkillMention } from '../../utils/skillMentionUtils';
import { Add01Icon } from '@terra/ui-icons';
import {
  useSkillsAuditLabels,
  useSkillsDetailLabels,
  useSkillsEditorLabels,
  useSkillsListLabels,
  useSkillsVersionLabels,
} from '../../hooks/useSkillLabels';

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string')
    ? value
    : [];

export type SkillsContentModel = {
  applicationId: string;
  canAuthorSkills: boolean;
  listLabels: ReturnType<typeof useSkillsListLabels>;
  editorLabels: SkillsEditorLabels;
  versionLabels: ReturnType<typeof useSkillsVersionLabels>;
  auditLabels: ReturnType<typeof useSkillsAuditLabels>;
  explainerLabels: SkillsExplainerLabels;
  detailLabels: ReturnType<typeof useSkillsDetailLabels>;
  formatDate: (date: Date) => string;
  emptyStateMessage: string;
  handleOpenSkillEditor: (request: {
    mode: 'create' | 'edit';
    skillId?: string;
    initialDraft?: SkillDraft;
  }) => void;
  handleOpenSkillDetail: (skill: SkillSummaryDto) => void;
  toolbarActions: ReactNode;
};
export function useSkillsContentModel(): SkillsContentModel {
  const appTargetId = useAppTargetId();
  const { isFeatureFlagEnabled: canAuthorSkills } = useFeatureFlags(
    'CUSTOMER_SKILLS_ENABLED',
  );
  const tEditor = useTranslations('customSkills.editor');
  const tExplainer = useTranslations('customSkills.explainer');
  const formatter = useFormatter();
  const { tabs, openTab, setActiveTab } = useMainContentTabs();
  const { chatTabs, setActiveChatTabId, updateChatTab } = useChatTabs();
  const { createNewChatTab } = useCreateNewChatTab();
  const setDraft = useSkillDraftsStore((state) => state.setDraft);
  const listLabels = useSkillsListLabels();
  const editorLabels: SkillsEditorLabels = useSkillsEditorLabels();
  const versionLabels = useSkillsVersionLabels();
  const auditLabels = useSkillsAuditLabels();
  const explainerLabels: SkillsExplainerLabels = useMemo(
    () => ({
      eyebrow: tExplainer('eyebrow'),
      title: tExplainer('title'),
      paragraph: tExplainer('paragraph'),
      whenToUseTitle: tExplainer('whenToUseTitle'),
      whenToUseItems: [
        tExplainer('whenToUseItem1'),
        tExplainer('whenToUseItem2'),
        tExplainer('whenToUseItem3'),
      ],
      whenNotToUseTitle: tExplainer('whenNotToUseTitle'),
      whenNotToUseItems: [
        tExplainer('whenNotToUseItem1'),
        tExplainer('whenNotToUseItem2'),
        tExplainer('whenNotToUseItem3'),
      ],
      anatomyTitle: tExplainer('anatomyTitle'),
      anatomyDescription: tExplainer('anatomyDescription'),
      anatomyFrontmatterTitle: tExplainer('anatomyFrontmatterTitle'),
      anatomyFrontmatterBody: tExplainer('anatomyFrontmatterBody'),
      anatomyInstructionsTitle: tExplainer('anatomyInstructionsTitle'),
      anatomyInstructionsBody: tExplainer('anatomyInstructionsBody'),
      anatomyTriggerTitle: tExplainer('anatomyTriggerTitle'),
      anatomyTriggerBody: tExplainer('anatomyTriggerBody'),
      exampleTitle: tExplainer('exampleTitle'),
      exampleSnippet: tExplainer('exampleSnippet'),
      exampleCaption: tExplainer('exampleCaption'),
      toolValidationEyebrow: tExplainer('toolValidationEyebrow'),
      toolValidationTitle: tExplainer('toolValidationTitle'),
      toolValidationBody: tExplainer('toolValidationBody'),
      toolValidationBullets: toStringArray(
        tExplainer.raw('toolValidationBullets'),
      ),
      tenantScopeTitle: tExplainer('tenantScopeTitle'),
      tenantScopeBody: tExplainer('tenantScopeBody'),
    }),
    [tExplainer],
  );
  const detailLabels = useSkillsDetailLabels();
  const formatDate = useCallback(
    (date: Date) =>
      formatter.dateTime(date, { dateStyle: 'medium', timeStyle: 'short' }),
    [formatter],
  );
  const emptyStateMessage = useMemo(
    () => listLabels.tenantScopeNote,
    [listLabels.tenantScopeNote],
  );
  const handleOpenSkillEditor = useCallback(
    ({
      mode,
      skillId,
      initialDraft,
    }: {
      mode: 'create' | 'edit';
      skillId?: string;
      initialDraft?: SkillDraft;
    }) => {
      const skillDraftId =
        mode === 'edit' && skillId ? `edit-${skillId}` : `create-${uuidv4()}`;
      const existingEditorTab = tabs.find(
        (existingTab) =>
          existingTab.type === 'skill-editor' &&
          existingTab.data.skillDraftId === skillDraftId,
      );
      const draft = initialDraft ?? EMPTY_SKILL_DRAFT;

      if (existingEditorTab) {
        setActiveTab(existingEditorTab.id);
      } else {
        setDraft(skillDraftId, draft);
        openTab({
          id: `skill-editor-${skillDraftId}`,
          title:
            draft.name ||
            (mode === 'create'
              ? editorLabels.createTitle
              : editorLabels.editTitle),
          type: 'skill-editor',
          data: { skillDraftId, mode, skillId },
        });
      }

      const existingChatTab = chatTabs.find(
        (chatTab) => chatTab.skillDraftId === skillDraftId,
      );
      const skillMention = createSkillMention({
        skillDraftId,
        skillId,
        mode,
        label: draft.name || draft.slug,
        fallbackLabel:
          mode === 'create' ? editorLabels.createTitle : editorLabels.editTitle,
      });

      if (existingChatTab) {
        updateChatTab(existingChatTab.tabId, { mentions: [skillMention] });
        setActiveChatTabId(existingChatTab.tabId);

        return;
      }

      createNewChatTab({
        skillDraftId,
        initialDraftInput:
          mode === 'create'
            ? tEditor('seedCreateChat')
            : tEditor('seedEditChat'),
        initialMentions: [skillMention],
      });
    },
    [
      chatTabs,
      createNewChatTab,
      editorLabels.createTitle,
      editorLabels.editTitle,
      openTab,
      setActiveChatTabId,
      setActiveTab,
      setDraft,
      tabs,
      tEditor,
      updateChatTab,
    ],
  );
  const handleOpenSkillDetail = useCallback(
    (skill: SkillSummaryDto) => {
      const existingSkillTab = tabs.find(
        (existingTab) =>
          existingTab.type === 'skill' &&
          existingTab.data.skillId === skill.skillId,
      );

      if (existingSkillTab) {
        setActiveTab(existingSkillTab.id);

        return;
      }

      openTab({
        id: `skill-${skill.skillId}`,
        title: skill.name || skill.slug,
        type: 'skill',
        data: { skillId: skill.skillId },
      });
    },
    [openTab, setActiveTab, tabs],
  );
  const handleCreateSkill = useCallback(() => {
    handleOpenSkillEditor({ mode: 'create' });
  }, [handleOpenSkillEditor]);
  const toolbarActions = useMemo(
    () =>
      canAuthorSkills ? (
        <Button size="xs" variant="outline" onClick={handleCreateSkill}>
          <Add01Icon size={14} />
          {listLabels.newSkill}
        </Button>
      ) : null,
    [canAuthorSkills, handleCreateSkill, listLabels.newSkill],
  );

  return {
    applicationId: appTargetId ?? '',
    canAuthorSkills,
    listLabels,
    editorLabels,
    versionLabels,
    auditLabels,
    explainerLabels,
    detailLabels,
    formatDate,
    emptyStateMessage,
    handleOpenSkillEditor,
    handleOpenSkillDetail,
    toolbarActions,
  };
}

type SkillsContentViewProps = {
  model: SkillsContentModel;
  showInlineToolbar?: boolean;
};

export function SkillsContentView({
  model,
  showInlineToolbar = false,
}: SkillsContentViewProps) {
  const {
    applicationId,
    canAuthorSkills,
    listLabels,
    editorLabels,
    versionLabels,
    auditLabels,
    explainerLabels,
    detailLabels,
    formatDate,
    emptyStateMessage,
    handleOpenSkillEditor,
    handleOpenSkillDetail,
    toolbarActions,
  } = model;

  return (
    <VStack gap={0} h="full" w="full" alignItems="stretch" minH="0">
      {showInlineToolbar && toolbarActions ? (
        <HStack justifyContent="flex-end" px="4" pt="4" w="full">
          {toolbarActions}
        </HStack>
      ) : null}
      <Box w="full" p="4" h="full" minH="0" flex="1" overflow="hidden">
        {applicationId ? (
          <SkillsManagerPanel
            listLabels={listLabels}
            editorLabels={editorLabels}
            versionLabels={versionLabels}
            auditLabels={auditLabels}
            explainerLabels={explainerLabels}
            detailLabels={detailLabels}
            formatDate={formatDate}
            applicationId={applicationId}
            onOpenSkillEditor={handleOpenSkillEditor}
            onOpenSkillDetail={handleOpenSkillDetail}
            showExplainer={false}
            canCreate={canAuthorSkills}
            canEdit={canAuthorSkills}
            scrollContent
          />
        ) : (
          <Box color="text.muted" py={2}>
            {emptyStateMessage}
          </Box>
        )}
      </Box>
    </VStack>
  );
}

export function SkillsContent() {
  const model = useSkillsContentModel();

  return <SkillsContentView model={model} showInlineToolbar />;
}
